import { useEffect, useState } from "react";
import { useModal } from "../context/ModalContext";
import {
  getAdditionalInfoById,
  saveAdditionalInfo,
  updateAdditionalInfo, // Import the update service
  uploadCompanyAdditionalInfoDocument, // Import the upload service for documents here if needed
} from "../services/companyService";
import { getAcceptedPaymentTerms } from "../services/companyService";
import {
  Trash2,
  Save,
  Loader2,
  ArrowLeft,
  ChevronsRight,
  Plus,
} from "lucide-react";
import Loading from "./Loading";
import { useToast } from "../context/ToastContext";
import FormNavigationButtons from "./FormNavigationButtons";

function AdditionalInfo({
  companyId,
  versionId,
  onNext,
  onBack,
  isEditable,
  currentStep,
  totalSteps,
}) {
  const [loading, setLoading] = useState(true);
  const [paymentTermOptions, setPaymentTermOptions] = useState([]);
  const [uploadingDocuments, setUploadingDocuments] = useState([]); // State for selected files
  const [saving, setSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [fallbackUsed, setFallbackUsed] = useState(false);

  const [formData, setFormData] = useState({
    preferredSupplierQualification: "",
    customerReference: "",
    acceptedIncoterms: "",
    acceptedPaymentTermId: 0,
    boardMembers: "",
    socialLinks: "",
    documents: [],
    additionalInfoId: 0,
    additionalInfo: "",
    version: versionId,
  });

  const { showModal } = useModal();
  const toast = useToast();

  const fetchOptions = async () => {
    const data = await getAcceptedPaymentTerms();
    setPaymentTermOptions(data);
  };

  const fetchAdditionalInfo = async () => {
    try {
      setLoading(true);
      let fetchVersion = versionId;
      let isFallback = false;
      let data = await getAdditionalInfoById(companyId, fetchVersion);

      if ((!data || data.length === 0) && fetchVersion > 1) {
        fetchVersion = fetchVersion - 1;
        isFallback = true;
        data = await getAdditionalInfoById(companyId, fetchVersion);
        setFallbackUsed(true);
      }

      if (data.length) {
        const entry = data[0];
        setFormData((prev) => ({
          ...prev,
          preferredSupplierQualification:
            entry.preferredSupplierQualification || "",
          customerReference:
            entry.customerReferences?.map((r) => r.value).join(", ") || "",
          acceptedIncoterms:
            entry.acceptedIncoTerms?.map((i) => i.value).join(", ") || "",
          acceptedPaymentTermId:
            entry.acceptedPaymentTerms?.[0]?.acceptedPaymentTerms?.id || 0,
          boardMembers:
            entry.boardMembers?.map((b) => b.value).join(", ") || "",
          socialLinks: entry.socialLinks?.map((s) => s.url).join(", ") || "",
          documents: entry.documents || [],
          additionalInfoId: entry.id,
          additionalInfo: entry.additionalInfo || "",
        }));
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("Failed to fetch additional info", err);
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchAdditionalInfo();
    setIsChanged(false);
  }, [companyId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsChanged(true);
  };

  const handleDocumentChange = (e) => {
    setUploadingDocuments(Array.from(e.target.files));
    setIsChanged(true);
  };

  const handleSave = async (onSuccessNext = null, showPopup = true) => {
    const payload = {
      additionalInfoId: formData.additionalInfoId || 0, // Use 'id' for update
      companyId: parseInt(companyId),
      version: formData.version,
      additionalInfo: formData.additionalInfo || "",
      preferredSupplierQualification: formData.preferredSupplierQualification,
      acceptedPaymentTerms: formData.acceptedPaymentTermId
        ? [formData.acceptedPaymentTermId]
        : [],
      customerReferences: formData.customerReference
        ? formData.customerReference.split(",").map((val) => val.trim())
        : [],
      acceptedIncoTerms: formData.acceptedIncoterms
        ? formData.acceptedIncoterms.split(",").map((val) => val.trim())
        : [],
      boardMembers: formData.boardMembers
        ? formData.boardMembers.split(",").map((val) => val.trim())
        : [],
      socialLinks: formData.socialLinks
        ? formData.socialLinks.split(",").map((val) => val.trim())
        : [],
      documents: formData.documents,
    };
    const temp = {
      additionalInfoId: 0,
      companyId: 0,
      version: 0,
      preferredSupplierQualification: "string",
      customerReferences: ["string"],
      acceptedPaymentTerms: [0],
      acceptedIncoTerms: ["string"],
      boardMembers: ["string"],
      socialLinks: ["string"],
    };

    console.log("Payload being sent →", JSON.stringify(payload, null, 2));
    setSaving(true);
    try {
      const res = formData.additionalInfoId
        ? await updateAdditionalInfo(payload) // Use update service
        : await updateAdditionalInfo(payload);

      if (!res) {
        console.error("Additional info save/update failed");
        setSaving(false);
        setFallbackUsed(false);
        throw new Error("Save failed");
      }

      // Handle document upload here if needed, similar to FinancialAbility
      if (uploadingDocuments.length > 0) {
        for (const file of uploadingDocuments) {
          await uploadCompanyAdditionalInfoDocument(
            companyId,
            formData.version,
            formData.additionalInfoId || 0, // Use existing ID or 0 if new
            file
          );
        }
      }

      if (showPopup) {
        // showModal(
        //   "Saved Successfully",
        //   "Additional info saved successfully.",
        //   onSuccessNext
        // );
        toast.success("Additional info saved successfully.");
      } else {
        if (onSuccessNext) onSuccessNext(); // skip modal, go ahead
      }
      setSaving(false);
    } catch (err) {
      console.error("Save error:", err);
      setSaving(false);
      // showModal("Save Failed", "Failed to save additional info.");
      toast.error("Failed to save additional info.");
    } finally {
      setUploadingDocuments([]); // Clear selected files after save attempt
      setIsChanged(false);
    }
  };

  const renderInput = (label, value, onChange, placeholder = label) => (
    <div className="flex">
      <label className="block mb-1 text-sm sm:min-w-[150px]">{label}</label>
      <input
        className={`w-full px-4 py-2 border  rounded text-sm ${
          !isEditable ? "bg-gray-100" : ""
        }`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        disabled={!isEditable}
      />
    </div>
  );

  const renderFileInput = (label, onChange) => (
    <div className="sm:flex mb-3">
      <label className="block mb-1 text-sm sm:min-w-[150px]">{label}</label>
      <input
        type="file"
        multiple // Allow multiple file selection
        className="w-full text-sm"
        onChange={onChange}
      />
      {uploadingDocuments.length > 0 && (
        <div className="mt-2 text-sm text-gray-600">
          Selected files:{" "}
          {uploadingDocuments.map((file) => file.name).join(", ")}
        </div>
      )}
    </div>
  );

  const handleSaveOnly = () => {
    handleSave(); // ✅ Show modal
  };

  const handleSaveAndContinue = () => {
    handleSave(onNext, false); // ⛔ No modal
  };

  return loading ? (
    <Loading fullscreen={false} />
  ) : (
    <section className="px-15">
      <div className="bg-white p-6">
        <h2 className="text-lg font-semibold mb-4">Additional Info</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          {renderInput(
            "Accepted Incoterms",
            formData.acceptedIncoterms,
            (val) => handleChange("acceptedIncoterms", val)
          )}

          <div className="col-span-2">
            <label className="block mb-1 text-sm font-medium">
              Accepted Payment Term
            </label>
            <select
              className={`w-full px-4 py-2 border rounded text-sm ${
                !isEditable ? "bg-gray-100" : "bg-white "
              }`}
              value={formData.acceptedPaymentTermId}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  acceptedPaymentTermId: parseInt(e.target.value),
                }))
              }
              disabled={!isEditable}
            >
              <option value={0}>Select a payment term</option>
              {paymentTermOptions.map((term) => (
                <option key={term.id} value={term.id}>
                  {term.value}
                </option>
              ))}
            </select>
          </div>

          {renderInput(
            "Preferred Supplier Qualification",
            formData.preferredSupplierQualification,
            (val) => handleChange("preferredSupplierQualification", val)
          )}
          {renderInput(
            "Customer Reference",
            formData.customerReference,
            (val) => handleChange("customerReference", val)
          )}
          {renderInput("Board Members", formData.boardMembers, (val) =>
            handleChange("boardMembers", val)
          )}
          {renderInput("Social Links", formData.socialLinks, (val) =>
            handleChange("socialLinks", val)
          )}
          <div className="col-span-2 hidden">
            {renderInput("Additional Info", formData.additionalInfo, (val) =>
              handleChange("additionalInfo", val)
            )}
          </div>
          {!isEditable && (
            <div className="col-span-2">
              {renderFileInput(
                "Documents / Certifications:",
                handleDocumentChange
              )}
            </div>
          )}
          {isEditable && (
            <div className="col-span-2">
              {formData.documents.map((d, i) => {
                {
                  <span key={i}>{d}</span>;
                }
              })}
            </div>
          )}
        </div>

        {/* --- Buttons Section using the new component --- */}
        <FormNavigationButtons
          onBack={onBack}
          onNext={onNext}
          handleSaveOnly={() => handleSave(true, false)}
          handleSaveAndContinue={() => handleSave(true, true)}
          isEditable={isEditable}
          isChanged={isChanged}
          saving={saving}
          fallbackUsed={fallbackUsed}
          currentStep={currentStep} // Pass current step
          totalSteps={totalSteps} // Pass total steps
        />
        {/* --- End Buttons Section --- */}

        {/* Buttons */}
        <div className="flex flex-wrap gap-4 justify-between sm:justify-end mt-8 hidden">
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition duration-200 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {isEditable && (
            <button
              type="button"
              onClick={handleSaveOnly}
              disabled={!isChanged || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
                saving || !isChanged
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
              }`}
            >
              {saving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          )}

          {isEditable && (
            <button
              type="button"
              onClick={handleSaveAndContinue}
              disabled={!isChanged || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
                saving || !isChanged
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
              }`}
            >
              {saving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save & Continue
            </button>
          )}

          <button
            type="button"
            onClick={onNext}
            disabled={saving || (isEditable && isChanged && !fallbackUsed)}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              saving || (isEditable && isChanged && !fallbackUsed)
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer"
            }`}
          >
            <ChevronsRight className="w-4 h-4" />
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export default AdditionalInfo;
